import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { ApiError } from '@/lib/api-client';
import { useAuthStore } from '@/stores/authStore';
import { STRINGS } from '@/strings/strings';

/**
 * Sebastian ruling 2026-09-17 — verification-pending block on the
 * summary screen. Sits next to a disabled "Passt so" button when the
 * customer's email address is unverified. Shows:
 *   - the address on file
 *   - a "E-Mail nochmal senden" button (wired to the existing
 *     `useAuthStore.requestEmailVerification`, backed by the
 *     throttled `POST /api/auth/request-verification`, 3/hour)
 *   - inline 429 hint on throttled resends
 *   - a spam-folder line
 *
 * The poll already run by the summary route flips `customerEmailVerified`
 * to `true` server-side once the customer clicks the verify link — no
 * new poller is introduced here.
 */

const S = STRINGS.book.summaryReview.verificationPending;

type ResendState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'sending' }
  | { readonly kind: 'sent' }
  | { readonly kind: 'error'; readonly message: string };

export interface VerificationPendingBlockProps {
  readonly customerEmail: string;
}

export function VerificationPendingBlock({ customerEmail }: VerificationPendingBlockProps) {
  const requestEmailVerification = useAuthStore((s) => s.requestEmailVerification);
  const [state, setState] = useState<ResendState>({ kind: 'idle' });

  const handleResend = async () => {
    if (state.kind === 'sending') return;
    setState({ kind: 'sending' });
    try {
      await requestEmailVerification();
      setState({ kind: 'sent' });
    } catch (err) {
      const message =
        err instanceof ApiError && err.httpStatus === 429 ? S.throttled : S.genericError;
      setState({ kind: 'error', message });
    }
  };

  return (
    <div
      role="status"
      aria-live="polite"
      className="flex flex-col gap-3 rounded-lg border border-dream-navy/20 bg-dream-background p-4 text-sm text-dream-navy"
    >
      <p className="font-semibold">{S.headline}</p>
      <p>{S.bodyLine1(customerEmail)}</p>
      <p>{S.bodyLine2}</p>
      <p className="text-dream-navy/70">{S.spamHint}</p>
      {state.kind === 'sent' ? (
        <p className="text-dream-navy/80">{S.resendAck}</p>
      ) : (
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={() => void handleResend()}
            disabled={state.kind === 'sending'}
            className="inline-flex h-10 items-center justify-center rounded-lg border border-dream-navy px-4 text-sm font-semibold text-dream-navy hover:bg-dream-navy hover:text-dream-white disabled:opacity-60"
          >
            {state.kind === 'sending' ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                {S.resendPending}
              </span>
            ) : (
              S.resendCta
            )}
          </button>
          {state.kind === 'error' && (
            <p role="alert" className="text-error">
              {state.message}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
